/**
 * wavEncoder.ts
 *
 * Pure client-side WAV encoder.
 * Produces a standard RIFF/PCM 16-bit mono WAV Blob from a Float32Array of
 * normalised audio samples (-1.0 → +1.0) at the given sample rate.
 *
 * No external dependencies. Used only when a voice violation is detected
 * (not on every analysis frame).
 */

/** Write a 32-bit little-endian unsigned integer into a DataView */
function writeUint32LE(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true);
}

/** Write a 16-bit little-endian unsigned integer into a DataView */
function writeUint16LE(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true);
}

/**
 * Encode `samples` (Float32, mono, range [-1, 1]) at `sampleRate` Hz into a
 * PCM 16-bit mono WAV Blob.
 *
 * @param samples    Raw audio samples in the range [-1.0, +1.0].
 * @param sampleRate The sample rate in Hz (e.g. 16000).
 * @returns          A Blob with MIME type `audio/wav`.
 */
export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const numChannels = 1;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataByteLength = samples.length * bytesPerSample;

  // 44-byte RIFF/WAVE header + PCM data
  const buffer = new ArrayBuffer(44 + dataByteLength);
  const view = new DataView(buffer);

  // RIFF chunk descriptor
  // ChunkID: "RIFF"
  view.setUint8(0, 0x52); view.setUint8(1, 0x49); view.setUint8(2, 0x46); view.setUint8(3, 0x46);
  writeUint32LE(view, 4, 36 + dataByteLength); // ChunkSize
  // Format: "WAVE"
  view.setUint8(8, 0x57); view.setUint8(9, 0x41); view.setUint8(10, 0x56); view.setUint8(11, 0x45);

  // fmt sub-chunk
  // Subchunk1ID: "fmt "
  view.setUint8(12, 0x66); view.setUint8(13, 0x6d); view.setUint8(14, 0x74); view.setUint8(15, 0x20);
  writeUint32LE(view, 16, 16);              // Subchunk1Size (PCM = 16)
  writeUint16LE(view, 20, 1);              // AudioFormat: PCM = 1
  writeUint16LE(view, 22, numChannels);    // NumChannels
  writeUint32LE(view, 24, sampleRate);     // SampleRate
  writeUint32LE(view, 28, byteRate);       // ByteRate
  writeUint16LE(view, 32, blockAlign);     // BlockAlign
  writeUint16LE(view, 34, bitsPerSample);  // BitsPerSample

  // data sub-chunk
  // Subchunk2ID: "data"
  view.setUint8(36, 0x64); view.setUint8(37, 0x61); view.setUint8(38, 0x74); view.setUint8(39, 0x61);
  writeUint32LE(view, 40, dataByteLength); // Subchunk2Size

  // PCM samples — clamp and convert Float32 → Int16
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    // Scale to [-32767, 32767] (avoid -32768 to stay symmetric)
    const int16 = clamped < 0 ? clamped * 32767 : clamped * 32767;
    view.setInt16(offset, int16, true);
    offset += 2;
  }

  return new Blob([buffer], { type: "audio/wav" });
}
